import {
  ButtonGroup,
  Card,
  Collapse,
  Dropdown,
  DropdownButton,
  Spinner,
} from "react-bootstrap";
import React from "react";
import { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet-async";

import ReportsFilter from "./ReportsFilter.js";
import UsageTable from "./tables/UsageTable.js";
import { InvoiceTable, SimTable, IPAllocationTable } from "./ReportsTable.js";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../../contexts/NotyfContext.js";
import Loader from "../../../components/spinners/Loader.js";
import { useTranslation } from "react-i18next";
import { useWhitelabel } from "../../../contexts/WhitelabelContext.js";
import OrdersTable from "./tables/OrdersTable.js";
import PortfolioActivityTable from "./tables/PortfolioActivityTable.js";
import InvoiceListTable from "./tables/InvoiceListTable.js";
import CustomerSessionsTable from "./tables/CustomerSessionsTable.js";
import CustomerSIMOverview from "./tables/CustomerSIMOverview.js";
import TimeoutReportsTable from "./tables/TimeoutReportsTable.js";
import {
  getTimeoutReportLines,
  getTimeoutReports,
} from "../../../redux/slices/reports.js";
import TimeoutReportTable from "./tables/TimeoutReportTable.js";
import CustomerSIMList from "./tables/CustomerSIMList.js";
import AdminCustomerSIMOverview from "./tables/AdminCustomerSIMOverview.js";

const ReportsPage = ({ demoMode = false }) => {
  const [tableIsExpanded, setTableIsExpanded] = useState(false);
  const [tableIsShown, setTableIsShown] = useState(false);
  const [report, setReport] = useState("");
  const [usageType, setUsageType] = useState("");
  const [reportData, setReportData] = useState([]);
  const [groupByField, setGroupByField] = useState("");
  const [groupByPeriod, setGroupByPeriod] = useState("");
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { whitelabelLoading, faviconUrl, backgroundColor } = useWhitelabel();
  const [isReportLoading, setIsReportLoading] = useState(false);
  const timeoutReports = useSelector((state) => state.reports.timeoutReports);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [timeoutReportId, setTimeoutReportId] = useState(null);
  const [timeoutReportTableIsShown, setTimeoutReportTableIsShown] =
    useState(false);
  const isDemoOrLead = activeUser.role === "lead" || demoMode;

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [impersonationUser, user]);

  useEffect(() => {
    const fetchTimeoutReports = async () => {
      await dispatch(getTimeoutReports()).catch((error) => {
        notyf.open({
          type: "danger",
          message: error,
          ripple: true,
          dismissible: false,
          duration: 5000,
          position: {
            x: "center",
            y: "top",
          },
        });
      });
    };

    if (activeUser.id && !demoMode && activeUser.role !== "lead") {
      fetchTimeoutReports();
    }
  }, [activeUser]);

  useEffect(() => {
    const fetchTimeoutReportLines = async () => {
      setTimeoutReportTableIsShown(false);
      await dispatch(
        getTimeoutReportLines({
          id: timeoutReportId,
          page: page,
          pageSize: pageSize,
        })
      )
        .then(() => {
          setTimeout(() => {
            setTimeoutReportTableIsShown(true);
          }, 500);
        })
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        });
    };

    if (timeoutReportId && !demoMode && activeUser.role !== "lead") {
      fetchTimeoutReportLines();
    }
  }, [timeoutReportId, page, pageSize]);

  if (isLoading || whitelabelLoading) {
    return <Loader />;
  }
  return (
    <React.Fragment>
      <Helmet title="Reports">
        <link rel="icon" href={faviconUrl} />
      </Helmet>
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">{t("reports.title")}</h1>
        <Row>
          <Col md="12" lg="4" className="d-flex">
            <ReportsFilter
              tableIsExpanded={tableIsExpanded}
              tableIsShown={tableIsShown}
              setTableIsExpanded={setTableIsExpanded}
              setTableIsShown={setTableIsShown}
              report={report}
              setReport={setReport}
              usageType={usageType}
              setUsageType={setUsageType}
              groupByField={groupByField}
              setGroupByField={setGroupByField}
              reportData={reportData}
              setReportData={setReportData}
              setGroupByPeriod={setGroupByPeriod}
              setIsReportLoading={setIsReportLoading}
              demoMode={demoMode}
            />
          </Col>
          <Col md="12" lg="8">
            <Collapse in={timeoutReports.length > 0} dimension="height">
              <Card className="w-100">
                <Card.Body>
                  <TimeoutReportsTable
                    timeoutReports={timeoutReports}
                    setTimeoutReportId={setTimeoutReportId}
                    setTimeoutReportTableIsShown={setTimeoutReportTableIsShown}
                    demoMode={demoMode}
                  />
                </Card.Body>
              </Card>
            </Collapse>
            {!tableIsShown && !timeoutReportTableIsShown && isReportLoading && (
              <div className="d-flex justify-content-center align-items-center w-100">
                <Spinner
                  animation="border"
                  variant="primary"
                  style={{
                    "--dynamic-spinner-color": backgroundColor,
                  }}
                />
              </div>
            )}
            <Collapse in={tableIsShown} dimension="height">
              <Card className="w-100">
                <Card.Body>
                  {report === "usage" ? (
                    <UsageTable
                      usageType={usageType}
                      groupByField={groupByField}
                      data={reportData}
                      groupByPeriod={groupByPeriod}
                      demoMode={isDemoOrLead}
                    />
                  ) : report === "invoice" ? (
                    <InvoiceTable demoMode={isDemoOrLead} />
                  ) : report === "sim_list" ? (
                    <SimTable demoMode={isDemoOrLead} />
                  ) : report === "ip_allocation" ? (
                    <IPAllocationTable demoMode={isDemoOrLead} />
                  ) : report === "orders" ? (
                    <OrdersTable demoMode={isDemoOrLead} />
                  ) : report === "portfolio-activity" ? (
                    <PortfolioActivityTable demoMode={isDemoOrLead} />
                  ) : report === "invoice-list" ? (
                    <InvoiceListTable demoMode={isDemoOrLead} />
                  ) : report === "customer-sessions" ? (
                    <CustomerSessionsTable demoMode={isDemoOrLead} />
                  ) : report === "sim-overview" ? (
                    <CustomerSIMOverview demoMode={isDemoOrLead} />
                  ) : report === "sim-list" ? (
                    <CustomerSIMList demoMode={isDemoOrLead} />
                  ) : report === "customer-sim-overview" ? (
                    <AdminCustomerSIMOverview demoMode={isDemoOrLead} />
                  ) : null}
                </Card.Body>
              </Card>
            </Collapse>
            <Collapse in={timeoutReportTableIsShown} dimension="height">
              <Card className="w-100">
                <Card.Body>
                  <TimeoutReportTable
                    currentPageNumber={page}
                    pageSize={pageSize}
                    setPage={setPage}
                    setPageSize={setPageSize}
                    timeoutReportId={timeoutReportId}
                  />
                </Card.Body>
              </Card>
            </Collapse>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default ReportsPage;
